import React, { ReactElement, useMemo } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { MultipleAccountsPopover } from './MultipleAccountsPopover';
import { FilesPopover } from './FilesPopover';

import { ActivityLogListType } from '../types/activityLogType';

dayjs.extend(utc);

type ActivityLogItemType = {
  activityLog: ActivityLogListType;
};

function ActivityAllLogItem({
  activityLog,
}: ActivityLogItemType): ReactElement {
  const totalAccounts = useMemo(() => {
    const value = activityLog?.AccountCIDPID || '';
    return value.split(',').filter(Boolean).length;
  }, [activityLog?.AccountCIDPID]);

  const totalDocIds = useMemo(() => {
    const value = activityLog?.DocId || '';
    return value.split(',').filter(Boolean).length;
  }, [activityLog?.DocId]);

  return (
    <div className="relative flex flex-nowrap rounded bg-white shadow focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-blue-600 dark:bg-neutral-800 dark:focus-visible:outline-blue-400">
      <div className="flex flex-grow flex-wrap px-4 py-2">
        {/* Activity Date */}
        <div className="w-full p-2 sm:w-1/2 md:w-1/3 lg:w-[20%]">
          <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
            Activity Date
          </label>
          <p className="text-xs font-bold tracking-wide text-black dark:text-white">
            {dayjs(activityLog?.CreatedOn).format('MM/DD/YYYY H:mm:ss')}
          </p>
        </div>
        {totalAccounts > 1 ? (
          <div className="w-full p-2 sm:w-1/2 md:w-1/3 lg:w-[10%]">
            <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
              Account
            </label>
            <p
              className={`text-xs font-bold tracking-wide text-black dark:text-white ${
                totalAccounts > 1 ? 'flex' : ''
              }`}
            >
              {activityLog?.AccountCIDPID !== null && (
                <MultipleAccountsPopover activityLog={activityLog} />
              )}
            </p>
          </div>
        ) : (
          <div className="w-full p-2 sm:w-1/2 md:w-1/3 lg:w-[10%]">
            <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
              Account CID - PID
            </label>
            <p className="text-xs font-bold tracking-wide text-black dark:text-white">
              {activityLog?.AccountCIDPID}
            </p>
          </div>
        )}

        <div className="w-full p-2 sm:w-1/2 md:w-1/3 lg:w-[15%]">
          <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
            Type (docs, person alert etc)
          </label>
          <p className="text-xs font-bold tracking-wide text-black dark:text-white">
            {activityLog?.Category}
          </p>
        </div>

        {/* Activity */}
        <div className="w-full p-2 sm:w-1/2 md:w-1/3 lg:w-[15%]">
          <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
            Activity
          </label>
          <p className="text-xs font-bold tracking-wide text-black dark:text-white">
            {activityLog?.Activity}
          </p>
        </div>

        <div className="w-full p-2 sm:w-1/2 md:w-1/3 lg:w-[20%]">
          <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
            ID (file name, person-id etc)
          </label>
          {activityLog?.Activity === 'CSV Download' ? (
            <p className="prevent-text-breakout text-xs font-bold tracking-wide text-black dark:text-white">
              {'CSV download - data not shown in log'}
            </p>
          ) : totalDocIds > 1 ? (
            <FilesPopover activityLog={activityLog} from="all" />
          ) : (
            <p className="prevent-text-breakout text-xs font-bold tracking-wide text-black dark:text-white">
              {activityLog?.DocId}
            </p>
          )}
        </div>

        {/* User */}
        <div className="w-full p-2 sm:w-1/2 md:w-1/3 lg:w-[10%]">
          <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
            User
          </label>
          <p className="prevent-text-breakout text-xs font-bold tracking-wide text-black dark:text-white">
            {activityLog?.UserName}
          </p>
        </div>
      </div>
    </div>
  );
}

export { ActivityAllLogItem };
